import { CreditApplicationMonitor, formatMoney } from '@dill/dill-shared';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  DillTable,
  DillColumn,
  DillButton,
  DillDropDownFilter,
  DillCheckbox,
} from '../../../general/Dill';
import { buildSupplierPath, hashStringToRange } from '../../../../utils/helpers';
import { APP_ENVIRONMENT } from '../../../../utils/constants';
import { useAppSelector } from '../../../../redux/store';

type MonitorTableRow = CreditApplicationMonitor & {
  riskLevelLabel: string;
  creditScoreLabel: string;
  averageDso: number;
  amountPastDue: number;
  // Amount Past Due ÷ Credit Approved - how much of the extended credit line is currently past
  // due, as a percent. Null when there's no credit approved to divide against.
  creditUtilization: number | null;
};

const createCheckboxItem = (label: string, value: string, isChecked: boolean) => ({
  label,
  value,
  labelInElement: (
    <div className="flex mx-1 my-1 items-center gap-2">
      <DillCheckbox isChecked={isChecked} />
      <div className="text-black text-xs">{label}</div>
    </div>
  ),
});

const CreditApplicationsMonitorTable = ({
  creditApplicationsMonitor,
}: {
  creditApplicationsMonitor: CreditApplicationMonitor[];
}) => {
  const navigate = useNavigate();
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] } | null>(null);

  const tableData: MonitorTableRow[] = useMemo(
    () =>
      creditApplicationsMonitor.map((row) => {
        const amountPastDue = hashStringToRange(`${row.id}-pastdue`, 0, 25000);
        return {
          ...row,
          riskLevelLabel: row?.nacmReport?.CmsCRScoreDetail?.ClassType ?? 'Risk N/A',
          creditScoreLabel: row?.nacmReport?.CmsCRScoreDetail?.Score ?? 'N/A',
          averageDso: hashStringToRange(`${row.id}-dso`, 18, 65),
          amountPastDue,
          creditUtilization: row.amount ? (amountPastDue / row.amount) * 100 : null,
        };
      }),
    [creditApplicationsMonitor]
  );

  const columns: DillColumn<MonitorTableRow>[] = useMemo(
    () => [
      {
        key: 'companyName',
        label: 'Name',
        sortable: true,
        getSortValue: (row) => row.companyName ?? '',
        getSearchValue: (row) => row.companyName ?? '',
        render: (value) => <p className="text-xs capitalize w-full">{String(value ?? '')}</p>,
      },
      {
        key: 'amount',
        label: 'Credit Approved',
        sortable: true,
        numeric: true,
        filterable: true,
        filterType: 'range',
        getSortValue: (row) => row.amount ?? 0,
        getSearchValue: (row) => String(row.amount ?? ''),
        render: (value) => (
          <p className="text-xs w-full text-right">{value != null ? formatMoney(value, 0) : '-'}</p>
        ),
      },
      {
        key: 'creditScore',
        label: 'Current Credit Score',
        sortable: true,
        filterable: true,
        filterKey: 'creditScoreLabel',
        getSortValue: (row) => row?.nacmReport?.CmsCRScoreDetail?.Score ?? 'N/A',
        getSearchValue: (row) => row?.nacmReport?.CmsCRScoreDetail?.Score ?? 'N/A',
        render: (_, row) => (
          <p className="text-xs w-full">{row?.nacmReport?.CmsCRScoreDetail?.Score ?? 'N/A'}</p>
        ),
      },
      {
        key: 'riskLevel',
        label: 'Risk Level',
        sortable: true,
        filterable: true,
        filterKey: 'riskLevelLabel',
        getSortValue: (row) => row?.nacmReport?.CmsCRScoreDetail?.ClassType ?? 'Risk N/A',
        getSearchValue: (row) => row?.nacmReport?.CmsCRScoreDetail?.ClassType ?? 'Risk N/A',
        render: (_, row) => {
          const riskClassType = row?.nacmReport?.CmsCRScoreDetail?.ClassType ?? 'Risk N/A';
          const riskClass = row?.nacmReport?.CmsCRScoreDetail?.Class;
          const status =
            riskClass === '1' || riskClass === '2'
              ? 'SUCCESS'
              : riskClass === '3'
              ? 'WARNING'
              : riskClass === '4A' || riskClass === '4B' || riskClass === '5'
              ? 'ERROR'
              : 'DISABLED';
          return (
            <div
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                status === 'SUCCESS'
                  ? 'bg-green-100 text-green-800'
                  : status === 'WARNING'
                  ? 'bg-yellow-100 text-yellow-800'
                  : status === 'ERROR'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
              {riskClassType}
            </div>
          );
        },
      },
      // Demo-only columns: hardcoded, deterministic-per-row placeholder values
      // (no backing data yet) to preview Credit + AR side-by-side. Hidden outside
      // the non-production/demo environment.
      ...(APP_ENVIRONMENT !== 'production'
        ? [
            {
              key: 'averageDso',
              label: 'Average DSO',
              sortable: true,
              numeric: true,
              filterable: true,
              filterType: 'range',
              getSortValue: (row) => row.averageDso,
              getSearchValue: (row) => String(row.averageDso),
              render: (_, row) => (
                <p className="text-xs w-full text-right">{row.averageDso} days</p>
              ),
            } as DillColumn<MonitorTableRow>,
            {
              key: 'amountPastDue',
              label: 'Amount Past Due',
              sortable: true,
              numeric: true,
              filterable: true,
              filterType: 'range',
              getSortValue: (row) => row.amountPastDue,
              getSearchValue: (row) => String(row.amountPastDue),
              render: (_, row) => (
                <p className="text-xs w-full text-right text-red-500 font-medium">
                  {row.amountPastDue > 0 ? formatMoney(row.amountPastDue, 2) : '-'}
                </p>
              ),
            } as DillColumn<MonitorTableRow>,
            {
              key: 'creditUtilization',
              label: 'Credit Utilization',
              sortable: true,
              numeric: true,
              filterable: true,
              filterType: 'range',
              getSortValue: (row) => row.creditUtilization ?? 0,
              getSearchValue: (row) => String(row.creditUtilization ?? ''),
              render: (_, row) => {
                if (row.creditUtilization == null) {
                  return <p className="text-xs w-full text-right text-gray-400">N/A</p>;
                }
                const status =
                  row.creditUtilization >= 80
                    ? 'ERROR'
                    : row.creditUtilization >= 50
                    ? 'WARNING'
                    : 'SUCCESS';
                return (
                  <p
                    className={`text-xs w-full text-right font-medium ${
                      status === 'ERROR'
                        ? 'text-red-600'
                        : status === 'WARNING'
                        ? 'text-yellow-700'
                        : 'text-green-700'
                    }`}>
                    {row.creditUtilization.toFixed(1)}%
                  </p>
                );
              },
            } as DillColumn<MonitorTableRow>,
          ]
        : []),
      {
        key: 'lastDetected',
        label: 'Last Detected',
        sortable: true,
        filterable: true,
        filterType: 'dateRange',
        filterKey: 'createdAt',
        getSortValue: (row) => (row.updatedAt ? new Date(row.updatedAt).getTime() : 0),
        getSearchValue: (row) => moment(row.createdAt).format('MM/DD/YYYY'),
        render: (_, row) => (
          <p className="text-xs w-full">{moment(row.createdAt).format('MM/DD/YYYY')}</p>
        ),
      },
      {
        key: 'action',
        label: 'Action',
        render: (_, row) => (
          <DillButton
            variant="secondary"
            label="Details"
            onClick={() =>
              navigate(buildSupplierPath(currentSupplier?.id, `/creditMonitoring/${row.id}`))
            }
          />
        ),
      },
    ],
    [navigate]
  );

  const [amountRange, setAmountRange] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  });
  const [creditScoreRange, setCreditScoreRange] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  });
  const [dsoRange, setDsoRange] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [pastDueRange, setPastDueRange] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  });
  const [utilizationRange, setUtilizationRange] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  });
  const [lastDetectedRange, setLastDetectedRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  const filteredData = useMemo(() => {
    let rows = tableData;

    if (searchValue) {
      const search = searchValue.toLowerCase();
      rows = rows.filter((row) => {
        const lastDetected = moment(row.createdAt).format('MM/DD/YYYY');
        return (
          row.companyName?.toLowerCase().includes(search) ||
          lastDetected.toLowerCase().includes(search) ||
          row.amount?.toString().toLowerCase().includes(search) ||
          row.creditScoreLabel.toString().toLowerCase().includes(search) ||
          row.riskLevelLabel.toString().toLowerCase().includes(search)
        );
      });
    }

    if (selectedFilters?.riskLevelLabel?.length) {
      rows = rows.filter((row) => selectedFilters.riskLevelLabel.includes(row.riskLevelLabel));
    }
    if (selectedFilters?.companyName?.length) {
      rows = rows.filter((row) => selectedFilters.companyName.includes(row.companyName || ''));
    }

    if (amountRange.min !== '') {
      rows = rows.filter((row) => (row.amount ?? 0) >= Number(amountRange.min));
    }
    if (amountRange.max !== '') {
      rows = rows.filter((row) => (row.amount ?? 0) <= Number(amountRange.max));
    }

    if (creditScoreRange.min !== '') {
      rows = rows.filter((row) => {
        const score = Number(row.creditScoreLabel);
        return !isNaN(score) && score >= Number(creditScoreRange.min);
      });
    }
    if (creditScoreRange.max !== '') {
      rows = rows.filter((row) => {
        const score = Number(row.creditScoreLabel);
        return !isNaN(score) && score <= Number(creditScoreRange.max);
      });
    }

    if (dsoRange.min !== '') {
      rows = rows.filter((row) => row.averageDso >= Number(dsoRange.min));
    }
    if (dsoRange.max !== '') {
      rows = rows.filter((row) => row.averageDso <= Number(dsoRange.max));
    }

    if (pastDueRange.min !== '') {
      rows = rows.filter((row) => row.amountPastDue >= Number(pastDueRange.min));
    }
    if (pastDueRange.max !== '') {
      rows = rows.filter((row) => row.amountPastDue <= Number(pastDueRange.max));
    }

    if (utilizationRange.min !== '') {
      rows = rows.filter((row) => (row.creditUtilization ?? 0) >= Number(utilizationRange.min));
    }
    if (utilizationRange.max !== '') {
      rows = rows.filter((row) => (row.creditUtilization ?? 0) <= Number(utilizationRange.max));
    }

    if (lastDetectedRange.start) {
      rows = rows.filter(
        (row) => row.createdAt && new Date(row.createdAt) >= new Date(lastDetectedRange.start)
      );
    }
    if (lastDetectedRange.end) {
      rows = rows.filter(
        (row) =>
          row.createdAt && new Date(row.createdAt) <= new Date(`${lastDetectedRange.end}T23:59:59`)
      );
    }

    return rows;
  }, [
    tableData,
    searchValue,
    selectedFilters,
    amountRange,
    creditScoreRange,
    dsoRange,
    pastDueRange,
    utilizationRange,
    lastDetectedRange,
  ]);

  const riskLevelOptions = useMemo(
    () => Array.from(new Set(tableData.map((row) => row.riskLevelLabel))).sort(),
    [tableData]
  );

  const companyNameOptions = useMemo(
    () =>
      Array.from(new Set(tableData.map((row) => row.companyName || '')))
        .filter(Boolean)
        .sort(),
    [tableData]
  );

  const amountRangeActiveLabel = useMemo(() => {
    if (amountRange.min === '' && amountRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? formatMoney(Number(v), 0) : '...');
    return `Credit Approved: ${fmt(amountRange.min)} – ${fmt(amountRange.max)}`;
  }, [amountRange]);

  const creditScoreRangeActiveLabel = useMemo(() => {
    if (creditScoreRange.min === '' && creditScoreRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? v : '...');
    return `Credit Score: ${fmt(creditScoreRange.min)} – ${fmt(creditScoreRange.max)}`;
  }, [creditScoreRange]);

  const dsoRangeActiveLabel = useMemo(() => {
    if (dsoRange.min === '' && dsoRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? v : '...');
    return `Average DSO: ${fmt(dsoRange.min)} – ${fmt(dsoRange.max)}`;
  }, [dsoRange]);

  const pastDueRangeActiveLabel = useMemo(() => {
    if (pastDueRange.min === '' && pastDueRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? formatMoney(Number(v), 0) : '...');
    return `Amount Past Due: ${fmt(pastDueRange.min)} – ${fmt(pastDueRange.max)}`;
  }, [pastDueRange]);

  const utilizationRangeActiveLabel = useMemo(() => {
    if (utilizationRange.min === '' && utilizationRange.max === '') return undefined;
    const fmt = (v: string) => (v !== '' ? `${v}%` : '...');
    return `Credit Utilization: ${fmt(utilizationRange.min)} – ${fmt(utilizationRange.max)}`;
  }, [utilizationRange]);

  const lastDetectedRangeActiveLabel = useMemo(() => {
    if (!lastDetectedRange.start && !lastDetectedRange.end) return undefined;
    const fmt = (d: string) => (d ? moment(d).format('MM/DD/YYYY') : '...');
    return `Last Detected: ${fmt(lastDetectedRange.start)} – ${fmt(lastDetectedRange.end)}`;
  }, [lastDetectedRange]);

  const filterItems = useMemo(
    () => [
      {
        id: 'companyName',
        label: 'Filter by Name',
        isSearchable: true,
        value: companyNameOptions.map((name) =>
          createCheckboxItem(name, name, selectedFilters?.companyName?.includes(name) ?? false)
        ),
      },
      {
        id: 'riskLevelLabel',
        label: 'Filter by Risk Level',
        value: riskLevelOptions.map((level) =>
          createCheckboxItem(
            level,
            level,
            selectedFilters?.riskLevelLabel?.includes(level) ?? false
          )
        ),
      },
      {
        id: 'amount',
        label: 'Filter by Credit Approved',
        value: [],
        activeLabel: amountRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Min</span>
              <input
                type="number"
                value={amountRange.min}
                onChange={(e) => setAmountRange((prev) => ({ ...prev, min: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Max</span>
              <input
                type="number"
                value={amountRange.max}
                onChange={(e) => setAmountRange((prev) => ({ ...prev, max: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(amountRange.min !== '' || amountRange.max !== '') && (
              <button
                onClick={() => setAmountRange({ min: '', max: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear amounts
              </button>
            )}
          </div>
        ),
      },
      {
        id: 'creditScore',
        label: 'Filter by Credit Score',
        value: [],
        activeLabel: creditScoreRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Min</span>
              <input
                type="number"
                value={creditScoreRange.min}
                onChange={(e) => setCreditScoreRange((prev) => ({ ...prev, min: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">Max</span>
              <input
                type="number"
                value={creditScoreRange.max}
                onChange={(e) => setCreditScoreRange((prev) => ({ ...prev, max: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(creditScoreRange.min !== '' || creditScoreRange.max !== '') && (
              <button
                onClick={() => setCreditScoreRange({ min: '', max: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear scores
              </button>
            )}
          </div>
        ),
      },
      ...(APP_ENVIRONMENT !== 'production'
        ? [
            {
              id: 'averageDso',
              label: 'Filter by Average DSO',
              value: [],
              activeLabel: dsoRangeActiveLabel,
              customContent: (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Min</span>
                    <input
                      type="number"
                      value={dsoRange.min}
                      onChange={(e) => setDsoRange((prev) => ({ ...prev, min: e.target.value }))}
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Max</span>
                    <input
                      type="number"
                      value={dsoRange.max}
                      onChange={(e) => setDsoRange((prev) => ({ ...prev, max: e.target.value }))}
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  {(dsoRange.min !== '' || dsoRange.max !== '') && (
                    <button
                      onClick={() => setDsoRange({ min: '', max: '' })}
                      className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                      Clear DSO
                    </button>
                  )}
                </div>
              ),
            },
            {
              id: 'amountPastDue',
              label: 'Filter by Amount Past Due',
              value: [],
              activeLabel: pastDueRangeActiveLabel,
              customContent: (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Min</span>
                    <input
                      type="number"
                      value={pastDueRange.min}
                      onChange={(e) =>
                        setPastDueRange((prev) => ({ ...prev, min: e.target.value }))
                      }
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Max</span>
                    <input
                      type="number"
                      value={pastDueRange.max}
                      onChange={(e) =>
                        setPastDueRange((prev) => ({ ...prev, max: e.target.value }))
                      }
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  {(pastDueRange.min !== '' || pastDueRange.max !== '') && (
                    <button
                      onClick={() => setPastDueRange({ min: '', max: '' })}
                      className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                      Clear amounts
                    </button>
                  )}
                </div>
              ),
            },
            {
              id: 'creditUtilization',
              label: 'Filter by Credit Utilization',
              value: [],
              activeLabel: utilizationRangeActiveLabel,
              customContent: (
                <div className="flex flex-col gap-2 pt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Min</span>
                    <input
                      type="number"
                      value={utilizationRange.min}
                      onChange={(e) =>
                        setUtilizationRange((prev) => ({ ...prev, min: e.target.value }))
                      }
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-8">Max</span>
                    <input
                      type="number"
                      value={utilizationRange.max}
                      onChange={(e) =>
                        setUtilizationRange((prev) => ({ ...prev, max: e.target.value }))
                      }
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  {(utilizationRange.min !== '' || utilizationRange.max !== '') && (
                    <button
                      onClick={() => setUtilizationRange({ min: '', max: '' })}
                      className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                      Clear utilization
                    </button>
                  )}
                </div>
              ),
            },
          ]
        : []),
      {
        id: 'lastDetected',
        label: 'Filter by Last Detected',
        value: [],
        activeLabel: lastDetectedRangeActiveLabel,
        customContent: (
          <div className="flex flex-col gap-2 pt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">From</span>
              <input
                type="date"
                value={lastDetectedRange.start}
                onChange={(e) =>
                  setLastDetectedRange((prev) => ({ ...prev, start: e.target.value }))
                }
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-8">To</span>
              <input
                type="date"
                value={lastDetectedRange.end}
                min={lastDetectedRange.start || undefined}
                onChange={(e) => setLastDetectedRange((prev) => ({ ...prev, end: e.target.value }))}
                className="flex-1 text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            {(lastDetectedRange.start || lastDetectedRange.end) && (
              <button
                onClick={() => setLastDetectedRange({ start: '', end: '' })}
                className="text-xs text-gray-400 hover:text-gray-600 text-left mt-0.5">
                Clear dates
              </button>
            )}
          </div>
        ),
      },
    ],
    [
      companyNameOptions,
      riskLevelOptions,
      selectedFilters,
      amountRange,
      amountRangeActiveLabel,
      creditScoreRange,
      creditScoreRangeActiveLabel,
      dsoRange,
      dsoRangeActiveLabel,
      pastDueRange,
      pastDueRangeActiveLabel,
      utilizationRange,
      utilizationRangeActiveLabel,
      lastDetectedRange,
      lastDetectedRangeActiveLabel,
    ]
  );

  const handleFilterChange = (data: Record<string, { value: string }[]>) => {
    const transformed = Object.fromEntries(
      Object.entries(data).map(([key, arr]) => [key, arr.map((item) => item.value)])
    );
    setSelectedFilters(transformed);
  };

  return (
    <DillTable<MonitorTableRow>
      columns={columns}
      data={filteredData}
      rowKey={(row) => row.id}
      sortable
      search
      searchPlaceholder="Search "
      searchValue={searchValue}
      onSearch={setSearchValue}
      toolbarLeft={
        <div className="w-[260px]">
          <DillDropDownFilter
            placeholder="Filters"
            closeMenu={false}
            isMulti
            showSelectedItems
            multiValue={selectedFilters}
            items={filterItems}
            showMinimalDesign
            onMultiSelectChange={handleFilterChange}
            inputHeight={30}
          />
        </div>
      }
      pagination
      resizable
      pageSize={20}
      striped={false}
      totalCount={filteredData.length}
      emptyMessage="No credit applications monitor report."
      onRowClick={(row) =>
        navigate(buildSupplierPath(currentSupplier?.id, `/creditMonitoring/${row.id}`))
      }
      showCount={false}
    />
  );
};

export default CreditApplicationsMonitorTable;
